/**
 * 笔画取数（R3，PLAN §6.1）
 * - 校验：1~10 个纯汉字
 * - 笔画来源：cnchar stroke（简体优先，覆盖常用字；未覆盖字返回 0，提示换字）
 * - 三段分配与取数公式固定如下（确定性、可复现，规则写入产品说明）：
 *     n=1  → G1=G2=G3=[该字]（单字派生）
 *     n=2  → G1=[w1], G2=[w2], G3=[w1,w2]（时数保证有成分）
 *     n≥3  → base=⌊n/3⌋, r=n%3；G1=base+(r≥1), G2=base+(r≥2), G3=base（尽量均分）
 *   月 = (ΣG1 − 1) % 12 + 1　日 = (ΣG2 − 1) % 30 + 1　时 = (ΣG3 − 1) % 12 + 1
 * - 日数超出农历当月实际天数时按小月上限收紧再推演
 */
import { divinate } from './engine';
/** cnchar 的 CJS 默认导出（bundle 端由构建器处理） */
import cnchar from 'cnchar';
/** 校验并取笔画（不抛错，返回结构化结果） */
export function strokeState(input) {
    const text = input.trim();
    if (text.length < 1) {
        return { ok: false, error: { code: 'NOT_HAN', message: '请输入汉字' } };
    }
    if (text.length > 10) {
        return { ok: false, error: { code: 'TOO_LONG', message: '最多输入 10 个汉字' } };
    }
    if (!/^[\u4e00-\u9fa5]+$/.test(text)) {
        return { ok: false, error: { code: 'NOT_HAN', message: '只能输入简体汉字' } };
    }
    const strokes = cnchar.stroke(text, 'array');
    // cnchar 对超纲字返回 0：提示换字
    const unknownChars = text.split('').filter((_, i) => (strokes[i] ?? 0) <= 0);
    if (unknownChars.length > 0) {
        return {
            ok: false,
            error: {
                code: 'UNKNOWN_CHAR',
                message: `以下字暂未收录，请更换：${unknownChars.join('、')}`,
                chars: unknownChars,
            },
        };
    }
    return { ok: true, strokes };
}
/**
 * 三段分配下标索引（闭区间），返回每段在原文字中的下标数组。
 * 规则见文件头注释（固定、可复现）。
 */
export function splitGroups(n) {
    if (n === 1)
        return [[0], [0], [0]];
    if (n === 2)
        return [[0], [1], [0, 1]];
    const base = Math.floor(n / 3);
    const r = n % 3;
    const g1 = base + (r >= 1 ? 1 : 0);
    const g2 = base + (r >= 2 ? 1 : 0);
    const g3 = base;
    // 按顺序切分（前段优先）
    const i1 = Array.from({ length: g1 }, (_, i) => i);
    const i2 = Array.from({ length: g2 }, (_, i) => g1 + i);
    const i3 = Array.from({ length: g3 }, (_, i) => g1 + g2 + i);
    return [i1, i2, i3];
}
/**
 * 笔画取数占卜。
 * @param input 1~10 个纯汉字
 * @param lunarYear 用于判断「当月实际天数」的农历年（2026 → 数字年）；默认取当前农历年。
 */
export function strokeDivination(input, lunarYear) {
    const state = strokeState(input);
    if (!state.ok)
        throw new Error(`笔画取数输入不合法：${state.error.message}`);
    const strokes = state.strokes;
    const [i1, i2, i3] = splitGroups(strokes.length);
    const sum = (idx) => idx.reduce((acc, i) => acc + (strokes[i] ?? 0), 0);
    let month = ((sum(i1) - 1) % 12) + 1;
    let day = ((sum(i2) - 1) % 30) + 1;
    const hour = ((sum(i3) - 1) % 12) + 1;
    // 日数超出农历当月实际天数（小月上限收紧）→ 重新推演
    // （规则固定：使用该农历年的第 month 月大小；year 缺省用当前农历年）
    const year = lunarYear ?? currentLunarYear();
    const maxDay = lunarMonthDayCount(year, month);
    if (day > maxDay)
        day = maxDay;
    return {
        input: input.trim(),
        strokes,
        groups: [i1.map((i) => strokes[i]), i2.map((i) => strokes[i]), i3.map((i) => strokes[i])],
        month,
        day,
        hour,
        result: divinate(month, day, hour),
    };
}
/**
 * 笔画明细文案：逐字列出笔画（如「服8 · 陶10 · 字6」）。
 * @param input 原取数文字（应与 strokes 一一对应）
 * @param strokes 每字笔画
 */
export function strokeBreakdown(input, strokes) {
    const chars = [...input.trim()];
    return chars.map((ch, i) => `${ch}${strokes[i] ?? '?'}`).join(' · ');
}
// ---------- 农历工具（lunar-javascript） ----------
import { Lunar, LunarYear } from 'lunar-javascript';
/** 当前农历年（数字，如 2026） */
export function currentLunarYear() {
    return Lunar.fromDate(new Date()).getYear();
}
/** 某农历年的某月（1..12）实际天数（大小月） */
export function lunarMonthDayCount(lunarYear, month) {
    const y = LunarYear.fromYear(lunarYear);
    const m = y.getMonth(month);
    if (!m)
        return 30; // 闰月等极端情况兜底
    return m.getDayCount();
}
export { Lunar, LunarYear };
//# sourceMappingURL=stroke.js.map